import React, { useCallback, useMemo } from 'react';
import { useMutation } from 'react-query';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import styled from 'styled-components';

import { EditIcon, TypeIcon } from 'src/Components/Icons';
import ErrorContainer, { parseAxiosError } from 'src/Layouts/ErrorContainer';
import TagRelate from 'src/Components/Tag/TagRelate';
import VirtualFields from 'src/Components/VirtualField/VirtualFieldList';
import { Spinner, Tag } from 'src/UIComponents';
import { updateSomething } from 'src/api-services';
import { SUPPORTED_ANYTHING_TYPES } from 'src/constants/app';
import { useSomething } from 'src/queries/something';

export const StyledTypeIcon = styled(TypeIcon)`
  margin-right: ${({ theme }) => theme.spacing.md};
`;

const EditLink = styled(Link)`
  text-decoration: none;
  opacity: 0.3;
  margin-left: ${({ theme }) => theme.spacing.md};

  :hover {
    opacity: 1;
  }
`;

function SomethingDetails({ id, ...rest }) {
  const { isLoading, refetch, error, data: something } = useSomething(id);

  const { mutate: updateSomethingMutation } = useMutation(updateSomething, {
    onSuccess: ({ data: updatedItem }) => {
      refetch();
    },
  });

  const clearTagRelation = useCallback(
    (tagId) => {
      updateSomethingMutation({
        id,
        removeTags: [tagId],
      });
    },
    [updateSomethingMutation, id]
  );

  const newTagRelation = useCallback(
    (tag) => {
      updateSomethingMutation({
        id,
        addTags: [tag.id],
      });
    },
    [updateSomethingMutation, id]
  );

  const anythingsByType = useMemo(() => {
    if (isLoading || !something || !something.Anythings) {
      return [];
    }
    return something.Anythings.sort((a, b) => {
      const timeA = new Date(a.createdAt).getTime();
      const timeB = new Date(b.createdAt).getTime();
      return timeA - timeB;
    }).reduce((acc, anything) => {
      const trimmedType = anything.type.trim();
      const type = SUPPORTED_ANYTHING_TYPES.includes(trimmedType)
        ? trimmedType
        : 'UNSUPPORTED';
      if (!acc[type]) {
        acc[type] = [];
      }
      acc[type].push(anything);
      return acc;
    }, {});
  }, [isLoading, something]);

  if (isLoading) {
    return (
      <Spinner
        style={{
          width: '100%',
          height: '80px',
        }}
      />
    );
  }

  if (error) {
    return <ErrorContainer {...parseAxiosError(error)} />;
  }

  return (
    <div {...rest}>
      <VirtualFields anythings={anythingsByType} />
      <h1
        title={`${something.createdAt}${something.createdAt !== something.updatedAt
            ? ` (${something.updatedAt})`
            : ''
          }`}
      >
        {something.title}
        <EditLink to={`/somethings/${id}/edit`} style={{ fontSize: '0.6em' }}>
          <EditIcon />
        </EditLink>
      </h1>
      {anythingsByType.IMAGE_URL &&
        anythingsByType.IMAGE_URL.map((anything) => {
          return (
            <Link
              key={`anything-${anything.id}`}
              to={`/anythings/${anything.id}`}
            >
              <img
                src={anything.value1}
                alt={anything.value2 || 'Image'}
                className="ml-2 u-pull-right"
                style={{
                  width: '35%',
                  minWidth: '200px',
                }}
              />
            </Link>
          );
        })}
      {anythingsByType.IMAGE_S3 &&
        anythingsByType.IMAGE_S3.map((anything) => {
          if (anything.value1.startsWith('http')) {
            return (
              <Link
                key={`anything-${anything.id}`}
                to={`/anythings/${anything.id}`}
              >
                <img
                  src={anything.value1}
                  alt={anything.value2 || 'Image'}
                  className="ml-2 u-pull-right"
                  style={{
                    width: '35%',
                    minWidth: '200px',
                  }}
                />
              </Link>
            );
          }
          return (
            <Link
              key={`anything-img-upload-${anything.id}`}
              to={`/anythings/${anything.id}`}
            >
              AWS S3 Image: {anything.value2}
            </Link>
          );
        })}
      {anythingsByType.URL &&
        anythingsByType.URL.map((anything) => {
          const httpLink = anything.value1.startsWith('http')
            ? anything.value1
            : `https://${anything.value1}`;
          const target = new URL(httpLink);
          const linkName =
            anything.value2 && anything.value2.trim() !== ''
              ? anything.value2
              : target.hostname.replace('www.', '');
          return (
            <div key={`anything-${anything.id}`}>
              <a
                href={target.href}
                target="_blank"
                rel="noreferrer"
                className="no-wrap"
              >
                <StyledTypeIcon type={anything.type} />
                {linkName}
              </a>
              <EditLink to={`/anythings/${anything.id}/edit`}>
                <EditIcon />
              </EditLink>
            </div>
          );
        })}
      {anythingsByType.DATE &&
        anythingsByType.DATE.map((anything) => {
          let date1 = new Date(anything.value1);
          let date2 = new Date(anything.value2 || '');
          let output = '';
          if (!isNaN(date1)) {
            output = date1.toLocaleDateString('sv');
          } else {
            output = <i>Not a date</i>;
          }
          if (!isNaN(date2)) {
            output = `${output} – ${date2.toLocaleDateString('sv')}`;
          } else if (anything.value2) {
            output = `${anything.value2}: ${output}`;
          }

          return (
            <div key={`anything-${anything.id}`}>
              <StyledTypeIcon type={anything.type} />
              {output}
              <EditLink to={`/anythings/${anything.id}/edit`}>
                <EditIcon />
              </EditLink>
            </div>
          );
        })}
      {anythingsByType.DATETIME &&
        anythingsByType.DATETIME.map((anything) => {
          let date1 = new Date(anything.value1);
          let date2 = new Date(anything.value2 || '');
          let output = '';
          if (!isNaN(date1)) {
            output = date1.toLocaleString('sv');
          } else {
            output = <i>Not a date</i>;
          }
          if (!isNaN(date2)) {
            output = `${output} – ${date2.toLocaleString('sv')}`;
          } else if (anything.value2) {
            output = `${anything.value2}: ${output}`;
          }

          return (
            <div key={`anything-${anything.id}`}>
              <StyledTypeIcon type={anything.type} />
              {output}
              <EditLink to={`/anythings/${anything.id}/edit`}>
                <EditIcon />
              </EditLink>
            </div>
          );
        })}
      {anythingsByType.POSITION &&
        anythingsByType.POSITION.map((anything) => {
          const latitude = anything.value1;
          const longitude = anything.value2;

          return (
            <div key={`anything-${anything.id}`}>
              <Link to={`/anythings/${anything.id}`}>
                <StyledTypeIcon type={anything.type} />
                Lat: <i>{latitude}</i>, Lng: <i>{longitude}</i>
              </Link>
            </div>
          );
        })}
      {anythingsByType.LOCATION &&
        anythingsByType.LOCATION.map((anything) => {
          const locationName = anything.value1;

          return (
            <div key={`anything-${anything.id}`}>
              <Link to={`/anythings/${anything.id}`}>
                <StyledTypeIcon type={anything.type} />
                {locationName}
              </Link>
            </div>
          );
        })}
      {anythingsByType.LENGTH &&
        anythingsByType.LENGTH.map((anything) => {
          const length = anything.value1;
          const unit = anything.value2;

          return (
            <div key={`anything-${anything.id}`}>
              <b>
                <StyledTypeIcon type={anything.type} />
                {length} {unit}
              </b>
              <EditLink to={`/anythings/${anything.id}/edit`}>
                <EditIcon />
              </EditLink>
            </div>
          );
        })}
      {anythingsByType.TEMPATURE &&
        anythingsByType.TEMPATURE.map((anything) => {
          const tempature = anything.value1;
          const unit = anything.value2 || '°C';

          return (
            <div key={`anything-${anything.id}`}>
              <b>
                <StyledTypeIcon type={anything.type} />
                {tempature} {unit}
              </b>
              <EditLink to={`/anythings/${anything.id}/edit`}>
                <EditIcon />
              </EditLink>
            </div>
          );
        })}
      {anythingsByType.WEIGHT &&
        anythingsByType.WEIGHT.map((anything) => {
          const weight = anything.value1;
          const unit = anything.value2;

          return (
            <div key={`anything-${anything.id}`}>
              <b>
                <StyledTypeIcon type={anything.type} />
                {weight} {unit}
              </b>
              <EditLink to={`/anythings/${anything.id}/edit`}>
                <EditIcon />
              </EditLink>
            </div>
          );
        })}
      {anythingsByType.PRICE &&
        anythingsByType.PRICE.map((anything) => {
          const amount = anything.value1;
          const unit = anything.value2 || 'kr';

          return (
            <div key={`anything-${anything.id}`}>
              <b>
                <StyledTypeIcon type={anything.type} />
                {amount} {unit}
              </b>
              <EditLink to={`/anythings/${anything.id}/edit`}>
                <EditIcon />
              </EditLink>
            </div>
          );
        })}
      {anythingsByType.TIME &&
        anythingsByType.TIME.map((anything) => {
          const time = anything.value1;
          const unit = anything.value2;

          return (
            <div key={`anything-${anything.id}`}>
              <b>
                <StyledTypeIcon type={anything.type} />
                {time} {unit}
              </b>
              <EditLink to={`/anythings/${anything.id}/edit`}>
                <EditIcon />
              </EditLink>
            </div>
          );
        })}
      {anythingsByType.AUTHOR &&
        anythingsByType.AUTHOR.map((anything) => {
          const authorName = anything.value1;
          const rest = anything.value2;

          return (
            <div key={`anything-${anything.id}`}>
              <b>
                <StyledTypeIcon type={anything.type} />
                {authorName} {rest && `: ${rest}`}
              </b>
              <EditLink to={`/anythings/${anything.id}/edit`}>
                <EditIcon />
              </EditLink>
            </div>
          );
        })}
      {anythingsByType.TEXT &&
        anythingsByType.TEXT.map((anything) => (
          <div key={`anything-${anything.id}`}>
            <EditLink
              to={`/anythings/${anything.id}/edit`}
              style={{ float: 'right' }}
            >
              <EditIcon />
            </EditLink>
            <ReactMarkdown>{anything.value1}</ReactMarkdown>
          </div>
        ))}
      <br />
      {something.Tags.map((tag) => (
        <Link
          to={`/tags/${tag.id}`}
          key={`tag-${tag.id}`}
          className="mr-1 mb-1"
          style={{
            display: 'inline-block',
          }}
        >
          <Tag color={tag.color} onDelete={() => clearTagRelation(tag.id)}>
            {tag.title}
          </Tag>
        </Link>
      ))}
      <TagRelate
        className="mt-2"
        onSelected={(tag) => newTagRelation(tag)}
        exclude={something.Tags.map((tag) => tag.id)}
      />
      {anythingsByType.UNSUPPORTED && anythingsByType.UNSUPPORTED.length > 0 && (
        <>
          <h5 className="mt-3">Unsupported Anythings</h5>
          {anythingsByType.UNSUPPORTED.map((anything) => (
            <div className="row" key={`something-anything-${anything.id}`}>
              <div className="three columns">
                <i>{anything.type}</i>
              </div>
              <div className="three columns">
                {anything.value1 ? anything.value1 : <span>&nbsp;</span>}
              </div>
              <div className="three columns">
                {anything.value2 ? anything.value2 : <span>&nbsp;</span>}
              </div>
              <div className="three columns txt-right">
                <Link to={`/anythings/${anything.id}`}>View</Link>
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

export default SomethingDetails;
